import { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { useElementsRefContext } from '@/app/shared/context/ElementsRefContext';
import { useReduxState } from '@/app/shared/context/ReduxStoreContext';

import { useScrollToTopWithCallback } from '@/packages/react-utils/useScrollToTopWithCallback';
import { listingLayoutFiltersHeightSelector } from '@/packages/redux-state/store/slices/listingLayout';
import { useListingLayoutViewModeSelector } from '@/packages/redux-state/store/slices/listingLayout/selectors/dumb';

export function useCalculateMapSizes(): void {
  const { mapContainerRef } = useElementsRefContext();
  const filtersHeight = useSelector(listingLayoutFiltersHeightSelector);
  const reduxState = useReduxState();
  const viewMode = useListingLayoutViewModeSelector();
  const scrollToTopWithCallback = useScrollToTopWithCallback();

  const updateMapSizes = useCallback(
    (filtersHeightValue: number) => {
      mapContainerRef.current?.style.setProperty('--map-top-offset', `${filtersHeightValue}px`);
      mapContainerRef.current?.style.setProperty('--map-height', `${window.innerHeight - filtersHeightValue}px`);
    },
    [mapContainerRef],
  );

  useEffect(() => {
    if (viewMode === 'list') {
      return;
    }

    scrollToTopWithCallback(() => updateMapSizes(filtersHeight));
  }, [filtersHeight, scrollToTopWithCallback, updateMapSizes, viewMode]);

  useEffect(() => {
    const handleResize = (): void => {
      const filtersHeightValue = listingLayoutFiltersHeightSelector(reduxState.getState());

      updateMapSizes(filtersHeightValue);
    };

    window.addEventListener('resize', handleResize);

    return (): void => {
      window.removeEventListener('resize', handleResize);
    };
  }, [mapContainerRef, reduxState, updateMapSizes]);
}
